import { useMemo, useState } from "react";
import intl from "react-intl-universal";
import { TbChevronDown, TbSearch, TbWallet } from "react-icons/tb";
import { formatStable } from "../../../Stables/constants";
import { BaasStablesNetworkBadge } from "./BaasStablesNetworkBadge";
import { BaasStablesWalletStatusBadge } from "./BaasStablesWalletStatusBadge";

const filterWallets = (wallets, query) => {
  const normalized = String(query ?? "").trim().toLowerCase();
  if (!normalized) return wallets;

  return wallets.filter((wallet) => {
    const haystack = [
      wallet.alias,
      wallet.asset,
      wallet.network,
      wallet.entityName,
      wallet.accountAlias,
      wallet.accountNumber,
      wallet.address,
      wallet.id,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return haystack.includes(normalized);
  });
};

const WalletListItem = ({ wallet, isSelected, onSelect }) => (
  <li>
    <button
      type="button"
      role="option"
      aria-selected={isSelected}
      className={`flex w-full flex-col gap-2 border-b border-gray-50 px-4 py-3 text-left transition last:border-b-0 hover:bg-brand-cream/60 dark:border-brand-dark-border dark:hover:bg-brand-dark-elevated/80 ${
        isSelected ? "bg-brand-cream/80 dark:bg-brand-dark-elevated" : ""
      }`}
      onClick={() => onSelect(wallet)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-brand-dark dark:text-gray-100">
            {wallet.alias}
          </p>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {wallet.entityName} · {wallet.accountAlias}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
          <BaasStablesNetworkBadge network={wallet.network} />
          <BaasStablesWalletStatusBadge status={wallet.status} />
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="font-medium text-brand-dark dark:text-gray-200">{wallet.asset}</span>
        <span className="font-semibold text-emerald-600 dark:text-emerald-400">
          {intl.get("baas.stables.operation.walletSelector.availableBalance", {
            amount: `${formatStable(wallet.balances?.available ?? 0)} ${wallet.asset}`,
          })}
        </span>
      </div>
    </button>
  </li>
);

const WalletSearchField = ({ search, onSearchChange, autoFocus = false }) => (
  <div className="relative">
    <TbSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
    <input
      type="search"
      value={search}
      onChange={(event) => onSearchChange(event.target.value)}
      placeholder={intl.get("baas.stables.operation.walletSelector.searchPlaceholder")}
      className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm outline-none focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated"
      autoFocus={autoFocus}
    />
  </div>
);

const WalletOptionsList = ({ wallets, value, onSelect, emptyMessage, maxHeightClass }) => (
  <ul className={`${maxHeightClass} overflow-y-auto py-1`} role="listbox">
    {wallets.length === 0 ? (
      <li className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
        {emptyMessage}
      </li>
    ) : (
      wallets.map((wallet) => (
        <WalletListItem
          key={wallet.id}
          wallet={wallet}
          isSelected={wallet.id === value}
          onSelect={onSelect}
        />
      ))
    )}
  </ul>
);

export const BaasStablesWalletSelector = ({
  wallets = [],
  value = null,
  onChange,
  disabled = false,
  compact = false,
  variant,
}) => {
  const resolvedVariant = variant ?? (compact ? "compact" : "default");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedWallet = useMemo(
    () => wallets.find((item) => item.id === value) ?? null,
    [wallets, value],
  );

  const filteredWallets = useMemo(
    () => filterWallets(wallets, search),
    [wallets, search],
  );

  const handleSelect = (wallet) => {
    onChange?.(wallet.id);
    setOpen(false);
    setSearch("");
  };

  const handleClose = () => {
    setOpen(false);
    setSearch("");
  };

  const emptyMessage = intl.get("baas.stables.operation.walletSelector.empty");

  if (resolvedVariant === "default") {
    return (
      <section className="app-card p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#e7a73f]">
              {intl.get("baas.stables.operation.walletSelector.eyebrow")}
            </p>
            <h2 className="mt-1 text-base font-bold text-brand-dark dark:text-gray-100">
              {intl.get("baas.stables.operation.walletSelector.title")}
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {intl.get("baas.stables.operation.walletSelector.subtitle")}
            </p>
          </div>
          <span className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            {intl.get("baas.stables.operation.backofficeBadge")}
          </span>
        </div>

        <div className="mt-4 space-y-3">
          <WalletSearchField search={search} onSearchChange={setSearch} />
          <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-brand-dark-border">
            <WalletOptionsList
              wallets={filteredWallets}
              value={value}
              onSelect={handleSelect}
              emptyMessage={emptyMessage}
              maxHeightClass="max-h-80"
            />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative min-w-0">
      <p className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">
        {intl.get("baas.stables.operation.walletSelector.changeWallet")}
      </p>

      <div className="relative">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 text-left transition hover:border-[#e7a73f]/50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-brand-dark-border dark:bg-brand-dark-elevated"
          onClick={() => !disabled && setOpen((current) => !current)}
          disabled={disabled}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          {selectedWallet ? (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-brand-dark dark:text-gray-100">
                {selectedWallet.alias}
              </p>
              <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
                {selectedWallet.asset} · {selectedWallet.network} · {selectedWallet.entityName}
              </p>
            </div>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <TbWallet className="h-4 w-4 shrink-0" />
              {intl.get("baas.stables.operation.walletSelector.placeholder")}
            </div>
          )}
          <TbChevronDown
            className={`h-4 w-4 shrink-0 text-gray-400 transition ${open ? "rotate-180" : ""}`}
          />
        </button>

        {open ? (
          <>
            <div className="fixed inset-0 z-10" onClick={handleClose} aria-hidden="true" />
            <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-card dark:border-brand-dark-border dark:bg-brand-dark-surface">
              <div className="border-b border-gray-100 p-3 dark:border-brand-dark-border">
                <WalletSearchField search={search} onSearchChange={setSearch} autoFocus />
              </div>
              <WalletOptionsList
                wallets={filteredWallets}
                value={value}
                onSelect={handleSelect}
                emptyMessage={emptyMessage}
                maxHeightClass="max-h-72"
              />
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
};
